import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import ReactGA from 'react-ga';
import { bindActionCreators } from 'redux';
import {
	flattenDeep, isEqual, map, isUndefined, forEach, isEmpty, get, capitalize,
} from 'lodash';
import { push as pushAction } from 'react-router-redux';
import utils from '@utils';
import Actions, { thunks } from '@redux/payments';
import appActions from '@redux/app';
import { PaymentService } from '@services';
import { AddPaymentMethodModal } from '../../../components';
import { PaymentReceiptTable, PaymentMethods, ReviewPayment } from './components';

class PaymentSummary extends Component {
	constructor(props) {
		super(props);
		const { returnData, invoiceStatusId } = this.getFormattedData(props.invoiceSummary);

		this.state = {
			data                  : returnData || [],
			invoiceStatusId       : invoiceStatusId || '',
			paymentMethods        : [],
			loadingPaymentMethods : true,
			selectedMethod        : {},
		};
	}

	componentDidMount() {
		const { getInvoiceSummary } = this.props;
		this.getPaymentMethods();
		getInvoiceSummary();
	}

	UNSAFE_componentWillReceiveProps = ({ invoiceSummary }) => {
		const lastProps = this.props;
		if (!isEqual(invoiceSummary, lastProps.invoiceSummary)) {
			const { returnData, invoiceStatusId } = this.getFormattedData(invoiceSummary);
			this.setState({
				data : returnData,
				invoiceStatusId,
			});
		}
	};

	componentWillUnmount = () => {
		const { updateStoreProperties } = this.props;
		updateStoreProperties({ activeInvoiceIds : [] });
	};

	getPaymentMethods = () => {
		this.setState({ loadingPaymentMethods : true });

		PaymentService.getData()
			.then(({ data }) => {
				let paymentMethods = [];
				let selectedMethod = {};

				if (data) {
					paymentMethods = utils.setPaymentMethods(data);
					selectedMethod = paymentMethods.find((method) => method.type === 'card' && method.isDefault)
						|| paymentMethods.find((method) => method.isDefault)
						|| {};
				}

				this.setState({ loadingPaymentMethods : false, selectedMethod, paymentMethods });
			})
			.catch(({ response : { data } }) => {
				this.setState({ loadingPaymentMethods : false, selectedMethod : {}, paymentMethods : [] });
				utils.notify({
					type    : 'error',
					message : data ? data.Message : 'Error loading payment methods',
					timeOut : 1500,
				});
			});
	}

	getFormattedData = (rawData) => {
		const { user } = this.props;
		const {
			TransactionSummaryItems, TotalOfficerPay, TotalOrganizationFee, TotalJurisdictionFee, RKPayFee, Total, Organizations, Tax,
		} = rawData;
		let returnData = [];
		let invoiceStatusId = 4;

		const generateInvoiceItems = (items, itemType, transactionIndex) => items.map(({
			ItemHours, ItemRate, ItemName, ItemSubtotal,
		}, index) => ({
			type      : 'additional',
			rowId     : `${itemType}_invoice_item_${transactionIndex}_${index}`,
			label     : ItemName,
			hours     : parseFloat(ItemHours),
			rate      : parseFloat(ItemRate),
			amountDue : parseFloat(ItemSubtotal),
		}));

		if (!isUndefined(TransactionSummaryItems)) {
			if (TransactionSummaryItems.length) invoiceStatusId = TransactionSummaryItems[0].InvoiceStatusId;
			returnData = map(TransactionSummaryItems, (officerItem, index) => {
				const { AdditionalInvoiceItems } = officerItem;
				const TimeZoneId = get(user, ['TimeZoneId'], 'America/Chicago');
				let additionalItems = [];

				if (AdditionalInvoiceItems) additionalItems = generateInvoiceItems(AdditionalInvoiceItems, 'additional', index);

				const result = {
					rowId   : `officer_${index}`,
					invoice : {
						id       : officerItem.InvoiceId,
						statusId : officerItem.InvoiceStatusId,
					},
					dateTime : {
						date      : utils.parseUTCDateToTimeZone(officerItem.StartDateTime, TimeZoneId).format('ddd, MMM D, YYYY'),
						startTime : utils.parseUTCDateToTimeZone(officerItem.StartDateTime, TimeZoneId).format('h:mm A'),
						endTime   : utils.parseUTCDateToTimeZone(officerItem.EndDateTime, TimeZoneId).format('h:mm A'),
					},
					location    : officerItem.Location,
					officername : {
						name  : officerItem.Officer ? officerItem.Officer : '',
						image : officerItem.OfficerPhotoUrl ? officerItem.OfficerPhotoUrl : '',
					},
					hours     : officerItem.Hours,
					rate      : officerItem.Rate,
					amountDue : officerItem.Amount,
				};

				return [result, additionalItems];
			});

			returnData.push({
				rowId     : 'TotalOfficerPay',
				type      : 'total',
				label     : 'Total Officers Pay',
				amountDue : TotalOfficerPay,
			});

			if (!isEmpty(Organizations)) {
				forEach(Organizations, (item, index) => {
					returnData.push({
						rowId            : `coordinator${index}`,
						type             : 'coordinator',
						firstCoordinator : !index,
						officername      : {
							name  : item.Name,
							image : item.ProfilePhotoURL,
						},
						hours     : item.Hours,
						rate      : item.Rate,
						amountDue : item.Amount,
					});
				});
				returnData.push({
					rowId     : 'TotalCoordinatorFee',
					type      : 'total',
					label     : 'Total Coordinators Fees',
					amountDue : TotalOrganizationFee,
				});
			}
			returnData.push({
				rowId     : 'TotalJurisdictionFee',
				type      : 'total',
				label     : 'Total Jurisdiction Fees',
				amountDue : TotalJurisdictionFee,
			});
			returnData.push({
				rowId     : 'RKPayFee',
				type      : 'total',
				label     : 'RKPay Fees',
				amountDue : RKPayFee,
			});
			returnData.push({
				rowId     : 'tax',
				type      : 'total',
				label     : 'Tax',
				amountDue : Tax,
			});
			returnData.push({
				rowId     : 'Total',
				type      : 'total',
				label     : 'TOTAL',
				amountDue : Total,
			});
		}
		return { returnData : flattenDeep(returnData), invoiceStatusId };
	};

	openPaymentMethodsModal = () => {
		this._paymentMethodsModal.openModal();
	}

	openReviewPaymentModal = () => {
		this._reviewPaymentModal.openModal();

		ReactGA.event({
			category : 'Payments',
			action   : 'Clicked \'Approve Payment\' button on transaction summary screen.',
		});
	}

	openAddPaymentMethodModal = () => {
		const { setField } = this.props;
		this._paymentMethodsModal.resetModal();
		setField('addPayment', 'showAddPayment', true);
	}

	onPaymentMethodChange = (selection) => {
		const { selectedMethod } = this.state;

		if (selection.id !== selectedMethod.id) {
			this.setState({ selectedMethod : selection });

			// TODO In the future: Reload invoice data with selected payment method
			// getInvoiceSummary(invoiceIds);
		}
	}

	render() {
		const {
			data, invoiceStatusId, paymentMethods, loadingPaymentMethods, selectedMethod,
		} = this.state;
		const {
			loading, invoiceSummary, activeInvoiceIds, push, user,
		} = this.props;

		const {
			Subtotal = 0,
			RKPayFee = 0,
			Tax = 0,
			Total = 0,
		} = invoiceSummary;

		return (
			<div id="payment-summary" className="container">
				<div className="row">
					<div className="col-sm-12 header">
						<h1>
							<i className="rkf rkf-receipt" />
							&nbsp;
							Your Invoice
						</h1>
						<Choose>
							<When condition={invoiceStatusId === 4}>
								<span className="font-size-10 label label-warning">Pending Payments</span>
							</When>
							<When condition={invoiceStatusId === 6}>
								<span className="font-size-10 label label-success">Paid</span>
							</When>
						</Choose>
					</div>
				</div>
				<PaymentReceiptTable
					data={!loading ? data : []}
					loading={loading}
				/>
				<If condition={data.length}>
					<div className="row">
						<If condition={selectedMethod.id}>
							<div className="col-sm-12 text-right selected-method">
								<Choose>
									<When condition={selectedMethod.type === 'bank_account'}>
										{`Your ${selectedMethod.bankName} ending in ${selectedMethod.accNumber} will be charged.`}
									</When>
									<When condition={selectedMethod.type === 'card'}>
										{`Your ${capitalize(selectedMethod.cardType)} card ${selectedMethod.cardNumber} will be charged`}
									</When>
								</Choose>
							</div>
						</If>
						<Choose>
							<When condition={!loadingPaymentMethods && paymentMethods.length === 0}>
								<div className="col-xs-6 col-sm-3 col-sm-offset-9 col-md-2 col-md-offset-10">
									<button
										type="button"
										id="add-payment-method"
										onClick={this.openAddPaymentMethodModal}
										className="btn btn-link"
									>
										+ Add Payment Method
									</button>
								</div>
							</When>
							<When condition={!loadingPaymentMethods}>
								<div className="col-xs-6 col-sm-3 col-sm-offset-9 col-md-2 col-md-offset-10">
									<button
										type="button"
										id="change-payment-method"
										onClick={this.openPaymentMethodsModal}
										className="btn btn-link"
									>
										Change Payment Method
									</button>
								</div>
							</When>
						</Choose>
						<div className="col-xs-6 col-sm-3 col-sm-offset-9 col-md-2 col-md-offset-10">
							<button
								type="button"
								onClick={this.openReviewPaymentModal}
								className="btn btn-primary btn-block"
								disabled={invoiceStatusId === 6 || !selectedMethod.id}
								title={!selectedMethod.id ? 'Select a payment method to continue' : ''}
							>
								Pay
							</button>
						</div>
					</div>
				</If>
				<PaymentMethods
					ref={(ref) => { this._paymentMethodsModal = ref; }}
					paymentMethods={paymentMethods}
					selectedMethod={selectedMethod}
					onConfirm={this.onPaymentMethodChange}
					openAddPaymentMethodModal={this.openAddPaymentMethodModal}
				/>
				<ReviewPayment
					ref={(ref) => { this._reviewPaymentModal = ref; }}
					subTotal={Subtotal}
					taxes={Tax + RKPayFee}
					total={Total}
					activeInvoiceIds={activeInvoiceIds}
					paymentMethodId={selectedMethod.id || null}
					push={push}
				/>
				<AddPaymentMethodModal user={user} callback={this.getPaymentMethods} />
			</div>
		);
	}
}

const mapStateToProps = ({
	payments : {
		invoiceSummary,
		loading,
		activeInvoiceIds,
	},
	app: { user },
}) => ({
	invoiceSummary,
	user,
	loading : loading.invoiceSummary,
	activeInvoiceIds,
});

const mapDispatchToProps = (dispatch) => ({
	setField              : bindActionCreators(appActions, dispatch).setFormField,
	updateStoreProperties : bindActionCreators(Actions, dispatch).updateStoreProperties,
	getInvoiceSummary     : bindActionCreators(thunks, dispatch).getInvoiceSummary,
	push                  : bindActionCreators(pushAction, dispatch),
});

PaymentSummary.propTypes = {
	getInvoiceSummary     : PropTypes.func.isRequired,
	invoiceSummary        : PropTypes.object,
	loading               : PropTypes.bool,
	setField              : PropTypes.func.isRequired,
	updateStoreProperties : PropTypes.func.isRequired,
	activeInvoiceIds      : PropTypes.array,
	push                  : PropTypes.func.isRequired,
	user                  : PropTypes.object,
};

PaymentSummary.defaultProps = {
	invoiceSummary   : {},
	loading          : false,
	activeInvoiceIds : [],
	user             : {},
};
export default connect(mapStateToProps, mapDispatchToProps)(PaymentSummary);
